import React, { useState } from "react";

import { GRAPHQL_URL } from "../../config";
import { SEO } from "../../seo/SEO";
import styled from "styled-components";
import { useTypedResource } from "../../i18n/languagehandler";

const Section = styled.section`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  align-items: start;
  min-height: auto;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    align-items: start;
    min-height: auto;
  }
`;

const SingleColumnSection = styled.section`
  max-width: 1000px;
  margin: 24px auto 0;
  padding: 0 20px;
  display: block;
`;

const ImageContainer = styled.div`
  display: flex;
  align-items: start;
  justify-content: center;
  padding-top: 20px;
  @media (max-width: 900px) {
    padding-top: 0;
  }
`;

// Form removed (no backend)

const Info = styled.div`
  display: grid;
  gap: 10px;
`;

const Clickable = styled.button`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 10px 12px;
  text-align: left;
  cursor: pointer;
`;

const SideImage = styled.img`
  width: 100%;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  transform: translateY(0);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 14px 40px rgba(0, 0, 0, 0.12);
  }
`;

// Newsletter request moved to Lectures page

const SubscribeSection = styled.section`
  max-width: 1000px;
  margin: 40px auto 0;
  padding: 0 20px;
`;

const SubscribeCard = styled.div`
  position: relative;
  background: linear-gradient(180deg, #ffffff 0%, #f9fafb 100%);
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
  padding: 28px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  overflow: hidden;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 20px;
  }

  &:before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 6px;
    background: var(--primary);
  }
`;

const SubscribeTitle = styled.h2`
  color: var(--text);
  font-size: 28px;
  margin: 0 0 12px 0;
`;

const SubscribeDescription = styled.p`
  color: var(--text-secondary);
  font-size: 16px;
  line-height: 1.6;
  margin: 0;
`;

const SubscribeForm = styled.form`
  display: grid;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: grid;
  gap: 8px;
`;

const Label = styled.label`
  color: var(--text);
  font-size: 14px;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(55, 90, 127, 0.1);
  }
`;

const Textarea = styled.textarea`
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  min-height: 120px;
  resize: vertical;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(55, 90, 127, 0.1);
  }
`;

const SubmitButton = styled.button`
  background: var(--primary);
  color: #fff;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--primary-dark);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(55, 90, 127, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Message = styled.div<{ type: "success" | "error" }>`
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  background: ${(props) => (props.type === "success" ? "#d1fae5" : "#fee2e2")};
  color: ${(props) => (props.type === "success" ? "#065f46" : "#dc2626")};
  border: 1px solid
    ${(props) => (props.type === "success" ? "#a7f3d0" : "#fecaca")};
`;

const RequestCard = styled.div`
  margin-top: 20px;
  background: linear-gradient(180deg, #ffffff 0%, #f9fafb 100%);
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
  padding: 20px;
`;

export const Contact: React.FC = () => {
  const res = useTypedResource<{
    title: string;
    name: string;
    email: string;
    message: string;
    details: string;
    booking: string;
    newsletterTitle: string;
    newsletterDescription: string;
    newsletterFullName: string;
    newsletterEmail: string;
    newsletterDescriptionLabel: string;
    newsletterSubscribe: string;
    newsletterSuccess: string;
    newsletterError: string;
    newsletterEmailExists: string;
    subscribeTitle: string;
    subscribeDescription: string;
    subscribeButton: string;
    subscribeSuccess: string;
    subscribeExists: string;
  }>("contact");

  const [copied, setCopied] = useState<string | null>(null);
  // Newsletter request moved to Lectures page
  const [subscribeForm, setSubscribeForm] = useState({
    fullName: "",
    email: "",
  });
  const [subscribeMessage, setSubscribeMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [isSubscribing, setIsSubscribing] = useState(false);

  // Lecture request form (same as on Lectures page)
  const [lectureForm, setLectureForm] = useState({
    fullName: "",
    email: "",
    description: "",
  });
  const [lectureMessage, setLectureMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [isLectureSubmitting, setIsLectureSubmitting] = useState(false);

  const copy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(text);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  // Newsletter request moved to Lectures page
  const handleSubscribeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubscribing(true);
    setSubscribeMessage(null);

    try {
      const response = await fetch(GRAPHQL_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: `
            mutation CreateNewsletterSubscriber($fullName: String!, $email: String!) {
              createNewsletterSubscriber(fullName: $fullName, email: $email) {
                id
                fullName
                email
              }
            }
          `,
          variables: {
            fullName: subscribeForm.fullName,
            email: subscribeForm.email,
          },
        }),
      });

      const result = await response.json();
      if (result.errors) {
        const msg: string = result.errors[0].message || "";
        setSubscribeMessage({
          type: "error",
          text: msg.includes("already exists")
            ? res.subscribeExists
            : res.newsletterError,
        });
      } else {
        setSubscribeMessage({ type: "success", text: res.subscribeSuccess });
        setSubscribeForm({ fullName: "", email: "" });
      }
    } catch (err) {
      setSubscribeMessage({ type: "error", text: res.newsletterError });
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleLectureSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLectureSubmitting(true);
    setLectureMessage(null);

    try {
      const response = await fetch(GRAPHQL_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: `
            mutation CreateLectureRequest($fullName: String!, $email: String!, $description: String) {
              createLectureRequest(fullName: $fullName, email: $email, description: $description)
            }
          `,
          variables: {
            fullName: lectureForm.fullName,
            email: lectureForm.email,
            description: lectureForm.description || null,
          },
        }),
      });

      const result = await response.json();
      if (result.errors) {
        const msg: string = result.errors[0].message || "";
        setLectureMessage({
          type: "error",
          text: msg.includes("already exists")
            ? res.newsletterEmailExists
            : res.newsletterError,
        });
      } else {
        setLectureMessage({ type: "success", text: res.newsletterSuccess });
        setLectureForm({ fullName: "", email: "", description: "" });
      }
    } catch (err) {
      setLectureMessage({ type: "error", text: res.newsletterError });
    } finally {
      setIsLectureSubmitting(false);
    }
  };
  return (
    <main>
      <SEO title={res.title} description={res.details} path="/contact" />
      {/* Row 1: Kontakt */}
      <SingleColumnSection>
        <div>
          <h2>{res.title}</h2>
          <p>{res.details}</p>
          <Info>
            <Clickable onClick={() => copy("abikic@icloud.com")}>
              📧 abikic@icloud.com{" "}
              {copied === "abikic@icloud.com" ? "(copied)" : ""}
            </Clickable>
            <Clickable onClick={() => copy("+4529168216")}>
              📞 +45 29 16 82 16 {copied === "+4529168216" ? "(copied)" : ""}
            </Clickable>
            <Clickable onClick={() => copy("+4520400479")}>
              📞 +45 20 40 04 79 {copied === "+4520400479" ? "(copied)" : ""}
            </Clickable>
          </Info>
        </div>
      </SingleColumnSection>

      {/* Row 2: Predavanje + Image */}
      <Section>
        <RequestCard>
          <SubscribeTitle>{res.newsletterTitle}</SubscribeTitle>
          <SubscribeDescription>
            {res.newsletterDescription}
          </SubscribeDescription>
          <SubscribeForm onSubmit={handleLectureSubmit}>
            <div style={{ marginBottom: "16px" }}></div>
            <FormGroup>
              <Label htmlFor="lrFullName">{res.newsletterFullName}</Label>
              <Input
                id="lrFullName"
                name="fullName"
                type="text"
                value={lectureForm.fullName}
                onChange={(e) =>
                  setLectureForm((p) => ({ ...p, fullName: e.target.value }))
                }
                required
                disabled={isLectureSubmitting}
                placeholder={res.newsletterFullName}
                autoComplete="name"
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="lrEmail">{res.newsletterEmail}</Label>
              <Input
                id="lrEmail"
                name="email"
                type="email"
                value={lectureForm.email}
                onChange={(e) =>
                  setLectureForm((p) => ({ ...p, email: e.target.value }))
                }
                required
                disabled={isLectureSubmitting}
                placeholder={res.newsletterEmail}
                autoComplete="email"
              />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="lrDesc">{res.newsletterDescriptionLabel}</Label>
              <Textarea
                id="lrDesc"
                name="description"
                value={lectureForm.description}
                onChange={(e) =>
                  setLectureForm((p) => ({
                    ...p,
                    description: e.target.value,
                  }))
                }
                placeholder={res.newsletterDescriptionLabel}
                disabled={isLectureSubmitting}
              />
            </FormGroup>
            <SubmitButton type="submit" disabled={isLectureSubmitting}>
              {isLectureSubmitting ? "..." : res.newsletterSubscribe}
            </SubmitButton>
            {lectureMessage && (
              <Message
                type={lectureMessage.type}
                role="status"
                aria-live="polite"
              >
                {lectureMessage.text}
              </Message>
            )}
          </SubscribeForm>
        </RequestCard>
        <ImageContainer>
          <SideImage
            src={process.env.PUBLIC_URL + "/assets/Contact/_MG_7844 2.jpg"}
            alt="Contact"
          />
        </ImageContainer>
      </Section>

      <SubscribeSection>
        <SubscribeCard>
          <div>
            <SubscribeTitle>{res.subscribeTitle}</SubscribeTitle>
            <SubscribeDescription>
              {res.subscribeDescription}
            </SubscribeDescription>
          </div>
          <div>
            <SubscribeForm onSubmit={handleSubscribeSubmit}>
              <FormGroup>
                <Label htmlFor="subFullName">{res.newsletterFullName}</Label>
                <Input
                  id="subFullName"
                  name="fullName"
                  type="text"
                  value={subscribeForm.fullName}
                  onChange={(e) =>
                    setSubscribeForm((p) => ({
                      ...p,
                      fullName: e.target.value,
                    }))
                  }
                  required
                  disabled={isSubscribing}
                  placeholder={res.newsletterFullName}
                  autoComplete="name"
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="subEmail">{res.newsletterEmail}</Label>
                <Input
                  id="subEmail"
                  name="email"
                  type="email"
                  value={subscribeForm.email}
                  onChange={(e) =>
                    setSubscribeForm((p) => ({ ...p, email: e.target.value }))
                  }
                  required
                  disabled={isSubscribing}
                  placeholder={res.newsletterEmail}
                  autoComplete="email"
                />
              </FormGroup>
              <SubmitButton type="submit" disabled={isSubscribing}>
                {isSubscribing ? "..." : res.subscribeButton}
              </SubmitButton>
              {subscribeMessage && (
                <Message
                  type={subscribeMessage.type}
                  role="status"
                  aria-live="polite"
                >
                  {subscribeMessage.text}
                </Message>
              )}
            </SubscribeForm>
          </div>
        </SubscribeCard>
      </SubscribeSection>
    </main>
  );
};
